import React, { useState, useMemo, memo, useCallback, useEffect } from 'react';
import ListingCard from '../components/ListingCard';
import SortDropdown from '../components/SortDropdown';
import { StyleConstants } from '../StyleConstants';
import { useActiveListings } from '../services/activeListingsDataService';
import '../GalleryMobile.css'; // Styles for mobile gallery view

// Modern card grid layout styles
const styles = {
  container: {
    width: '100%', 
    maxWidth: '100%',
    margin: 0,
    padding: 0, // No padding here since it's handled by the parent container
    backgroundColor: 'transparent', // Changed to transparent to let parent background show through
    boxSizing: 'border-box',
  },
  gridContainer: {
    marginTop: '8px',
    marginBottom: '32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px', /* Matches SplitView's spacing */
    marginTop: 0,
    padding: 0,
    width: '100%',
    boxSizing: 'border-box'
  },
  sectionTitle: {
    ...StyleConstants.sectionTitle,
    margin: 0,
    lineHeight: '1.15', /* Exactly matching SplitView's lineHeight */
    padding: 0
  },
  statusCounter: StyleConstants.statusCounter,
  noListings: {
    padding: '40px',
    textAlign: 'center',
    backgroundColor: 'var(--grey)',
    color: '#666',
    borderRadius: '8px',
    fontSize: '16px',
  }
};

function ActiveListingsGalleryView({ isMobile }) {
  const { listings, loading, error } = useActiveListings();
  const [sortOrder, setSortOrder] = useState('newest');
  const [priorityBoost, setPriorityBoost] = useState(false);

  const sortOptions = useMemo(() => ([
    { value: 'newest', label: 'Newest' },
    { value: 'price_high', label: '$ High → Low' },
    { value: 'price_low', label: '$ Low → High' }
  ]), []);

  const handleSortChange = useCallback((e) => {
    setSortOrder(e.target.value);
  }, []);

  // After a sort change, temporarily boost loading priority so the newly-sorted "top cards"
  // feel instant (important when switching to price sorts where those listings weren't
  // previously near the top / preloaded).
  useEffect(() => {
    setPriorityBoost(true);
    const t = setTimeout(() => setPriorityBoost(false), 2500);
    return () => clearTimeout(t);
  }, [sortOrder]);

  // Sort listings for display (default: newest)
  const sortedListings = useMemo(() => {
    if (!listings || !Array.isArray(listings)) return [];
    const next = [...listings];

    const getDays = (l) => (typeof l?.daysOnMarket === 'number' && Number.isFinite(l.daysOnMarket)) ? l.daysOnMarket : Number.POSITIVE_INFINITY;
    const getPrice = (l) => (typeof l?.price === 'number' && Number.isFinite(l.price)) ? l.price : Number.NaN;

    next.sort((a, b) => {
      if (sortOrder === 'price_high') {
        const ap = getPrice(a);
        const bp = getPrice(b);
        if (Number.isFinite(ap) && Number.isFinite(bp) && ap !== bp) return bp - ap;
        if (Number.isFinite(ap) && !Number.isFinite(bp)) return -1;
        if (!Number.isFinite(ap) && Number.isFinite(bp)) return 1;
        // tie-breaker: newest first
        return getDays(a) - getDays(b);
      }

      if (sortOrder === 'price_low') {
        const ap = getPrice(a);
        const bp = getPrice(b);
        if (Number.isFinite(ap) && Number.isFinite(bp) && ap !== bp) return ap - bp;
        if (Number.isFinite(ap) && !Number.isFinite(bp)) return -1;
        if (!Number.isFinite(ap) && Number.isFinite(bp)) return 1;
        // tie-breaker: newest first
        return getDays(a) - getDays(b);
      }

      // newest (lowest days on market first)
      return getDays(a) - getDays(b);
    });

    return next;
  }, [listings, sortOrder]);

  // Render the listings in a grid with optimized loading
  const renderListings = () => {
    if (loading) {
      return (
        <div style={{ ...styles.noListings, color: '#333' }}>
          Loading active listings...
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ ...styles.noListings, color: '#d32f2f' }}>
          Error loading listings: {error.message}
        </div>
      );
    }

    if (!sortedListings || sortedListings.length === 0) {
      return (
        <div style={styles.noListings}>
          No active listings found
        </div>
      );
    }

    return (
      <div 
        className="gallery-view-grid" 
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', // From StyleConstants
          width: '100%', // From StyleConstants
          gap: '32px', // From ListingList.jsx (override)
          marginTop: '8px', // From ListingList.jsx
          marginBottom: '32px' // From ListingList.jsx
          // NO justifyItems, NO padding, NO boxSizing as per ListingList.jsx effective style
        }}
      >
        {sortedListings.map((listing, index) => {
          const priorityCount = priorityBoost ? 24 : 8;
          const isPriority = index < priorityCount;
          const delay = priorityBoost ? 0 : index * 50;
          return (
          <ListingCard 
            key={listing.id} 
            listing={listing} 
            isMobile={isMobile}
            priority={isPriority} // Prioritize top cards (boosted briefly after sort changes)
            loadingDelay={delay} // Stagger loading for smoother experience (disabled during boost)
          />
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ padding: '0' }}>
      <div style={styles.container}>
        {/* Header with title and count */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{
              ...StyleConstants.sectionTitle,
              margin: 0,
              marginTop: 0,
              marginBottom: 0,
              padding: 0,
              lineHeight: '32px', /* Use exact px value from StyleConstants rather than relative value */
              fontSize: '24px', /* Explicitly set font size to match SplitView */
              fontWeight: '700' /* Explicitly set font weight to match SplitView */
            }}>
              Active
              <span style={StyleConstants.statusCounter}>
                ({sortedListings.length})
              </span>
            </h2>
          </div>

          {/* Sort Dropdown (same placement as day filter in OpenHouseGalleryView) */}
          <SortDropdown
            value={sortOrder}
            options={sortOptions}
            onChange={handleSortChange}
            viewContext="ActiveListingsGallery"
          />
        </div>
        
        {/* Show listings in a single grid */}
        {renderListings()}
      </div>
    </div>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(ActiveListingsGalleryView);

